/**
 * Copyright © 2025 Delicious AI, LLC
 */

import axios from 'axios'

export class ClientCredentialToken {
  accessToken: string

  expiresIn: number

  tokenType: string

  scope: string

  createdAt: number

  constructor(
    accessToken: string,
    expiresIn: number,
    tokenType: string,
    scope: string,
    createdAt: number = Date.now() / 1000,
  ) {
    this.accessToken = accessToken
    this.expiresIn = expiresIn
    this.tokenType = tokenType
    this.scope = scope
    this.createdAt = createdAt
  }

  /**
   * If the token is expired or will expire in the next 5 minutes, we return true,
   * indicating that a refresh is needed
   */
  needsRefresh(): boolean {
    const refreshThreshold = 60 * 5 // 5 minutes
    const elapsedTime = Date.now() / 1000 - this.createdAt
    const timeUntilExpiry = this.expiresIn - elapsedTime

    return timeUntilExpiry <= refreshThreshold
  }
}

export async function getAccessToken(
  url: string,
  clientId: string,
  clientSecret: string,
): Promise<ClientCredentialToken | null> {
  const payload = `${clientId}:${clientSecret}`
  const hashedCredentials = Buffer.from(payload).toString('base64')

  const headers = {
    Authorization: `Basic ${hashedCredentials}`,
    'Content-Type': 'application/x-www-form-urlencoded',
  }

  const data = new URLSearchParams()
  data.append('grant_type', 'client_credentials')

  try {
    const response = await axios.post(url, data, { headers })

    if (response.status === 200) {
      const token = response.data
      return new ClientCredentialToken(
        token.access_token,
        token.expires_in,
        token.token_type,
        token.scope,
      )
    }
    return null
  } catch (error) {
    console.error('Error getting access token:', error)
    return null
  }
}
